/**
 * Tracking Management tab: granular control over JSONCargo API tracking calls.
 *
 * Shows companies with active containers, per-company usage stats,
 * and allows syncing individual containers or all containers per company.
 */
import { useState, useMemo, useCallback, useRef } from "react";
import {
    RefreshCw05,
    ChevronDown,
    ChevronRight,
    CheckCircle,
    XCircle,
    AlertTriangle,
    SearchLg,
    Zap,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { Input } from "@/components/base/input/input";
import { Badge } from "@/components/base/badges/badges";
import {
    useTrackingCompanies,
    useTrackingCompanyContainers,
    useSyncEstimate,
    useSyncCompany,
    useSyncContainer,
} from "@/hooks/api/use-admin-tracking";
import { useTrackingTestRun } from "@/hooks/api/use-tracking-test-run";
import type {
    AdminTrackingFilters,
    CompanyTrackingInfo,
    ContainerTrackingInfo,
    SyncEstimate as SyncEstimateType,
    SyncResult,
} from "@/types/api/admin-tracking";
import type { TrackingTestRunRequest, TrackingTestStep } from "@/types/api/tracking";
import { Checkbox } from "@/components/base/checkbox/checkbox";

// ============================================================================
// Constants
// ============================================================================

const COMPANY_TYPE_OPTIONS = [
    { id: "all", label: "Tutti i tipi" },
    { id: "sender", label: "Sender" },
    { id: "receiver", label: "Receiver" },
    { id: "freight_forwarder", label: "Freight Forwarder" },
    { id: "carrier", label: "Carrier" },
    { id: "customs_broker", label: "Customs Broker" },
];

// ============================================================================
// Helpers
// ============================================================================

function formatCost(amount: number): string {
    return `$${amount.toFixed(2)}`;
}

function formatRelativeTime(dateStr: string | null): string {
    if (!dateStr) return "Mai";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMin < 1) return "Ora";
    if (diffMin < 60) return `${diffMin}m fa`;
    if (diffHours < 24) return `${diffHours}h fa`;
    if (diffDays < 7) return `${diffDays}g fa`;
    return date.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

function companyTypeBadgeColor(type: string): "brand" | "success" | "error" | "gray" | "warning" {
    switch (type) {
        case "sender": return "brand";
        case "receiver": return "success";
        case "freight_forwarder": return "warning";
        default: return "gray";
    }
}

function statusBadgeColor(status: string): "brand" | "success" | "error" | "gray" | "warning" {
    switch (status) {
        case "BOOKED": return "brand";
        case "IN_TRANSIT": return "warning";
        case "ARRIVED": return "success";
        case "DELIVERED": return "success";
        default: return "gray";
    }
}

function usageBarColor(pct: number): string {
    if (pct >= 90) return "bg-error-primary";
    if (pct >= 70) return "bg-warning";
    return "bg-success-primary";
}

// ============================================================================
// PayloadViewer (expandable key-value tree for test-run responses)
// ============================================================================

function PayloadViewer({ payload, defaultExpanded = false }: { payload: unknown; defaultExpanded?: boolean }) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [showRaw, setShowRaw] = useState(false);

    if (payload === null || payload === undefined) {
        return <span className="text-tertiary italic">(vuoto)</span>;
    }

    if (showRaw) {
        return (
            <div className="space-y-1">
                <button
                    type="button"
                    onClick={() => setShowRaw(false)}
                    className="text-xs text-brand-primary hover:underline"
                >
                    Mostra ad albero
                </button>
                <pre className="text-xs font-mono bg-secondary/50 p-3 rounded-lg overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all">
                    {JSON.stringify(payload, null, 2)}
                </pre>
            </div>
        );
    }

    if (typeof payload !== "object") {
        return <span className="font-mono text-sm">{String(payload)}</span>;
    }

    if (Array.isArray(payload)) {
        return (
            <div className="pl-2 border-l border-secondary">
                <button
                    type="button"
                    onClick={() => setExpanded((e) => !e)}
                    className="flex items-center gap-1 text-sm text-primary hover:text-brand-primary"
                >
                    {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    <span>Array ({payload.length} elementi)</span>
                </button>
                {expanded && (
                    <div className="mt-1 space-y-1 pl-3">
                        {payload.slice(0, 50).map((item, i) => (
                            <div key={i} className="flex gap-2">
                                <span className="text-tertiary shrink-0">[{i}]:</span>
                                <PayloadViewer payload={item} defaultExpanded={false} />
                            </div>
                        ))}
                        {payload.length > 50 && (
                            <span className="text-tertiary text-xs">... e altri {payload.length - 50} elementi</span>
                        )}
                    </div>
                )}
            </div>
        );
    }

    const keys = Object.keys(payload as object).sort();
    return (
        <div className="pl-2 border-l border-secondary">
            <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="flex items-center gap-1 text-sm text-primary hover:text-brand-primary"
            >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <span>Oggetto ({keys.length} chiavi)</span>
            </button>
            {expanded && (
                <div className="mt-1 space-y-1 pl-3">
                    {keys.map((k) => {
                        const v = (payload as Record<string, unknown>)[k];
                        const isObject = typeof v === "object" && v !== null;
                        return (
                            <div key={k} className="flex gap-2 text-sm">
                                <span className="text-tertiary shrink-0 font-medium">{k}:</span>
                                {isObject ? (
                                    <PayloadViewer payload={v} defaultExpanded={false} />
                                ) : (
                                    <PayloadViewer payload={v} defaultExpanded={false} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ============================================================================
// TrackingTestSection (Test API JSONCargo)
// ============================================================================

function TrackingTestSection() {
    const [testType, setTestType] = useState<"bl" | "container">("container");
    const [blNumber, setBlNumber] = useState("");
    const [containerNumber, setContainerNumber] = useState("");
    const [shippingLine, setShippingLine] = useState("MAERSK");
    const [noCache, setNoCache] = useState(true);
    const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
    const [treeViewStepIds, setTreeViewStepIds] = useState<Set<string>>(new Set());

    const testRun = useTrackingTestRun();
    const result = testRun.data ?? null;
    const error = testRun.error;

    const toggleStep = useCallback((id: string) => {
        setExpandedSteps((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const handleSubmit = useCallback(() => {
        const body: TrackingTestRunRequest = {
            type: testType,
            shipping_line: shippingLine.trim(),
            no_cache: noCache,
        };
        if (testType === "bl") {
            body.bl_number = blNumber.trim();
            if (containerNumber.trim()) body.container_number = containerNumber.trim();
        } else {
            body.container_number = containerNumber.trim();
        }
        testRun.mutate(body);
    }, [testType, blNumber, containerNumber, shippingLine, noCache, testRun]);

    const canSubmit =
        (testType === "bl"
            ? blNumber.trim() !== "" && shippingLine.trim() !== ""
            : containerNumber.trim().length >= 10);

    return (
        <div className="rounded-lg border border-secondary bg-primary p-6">
            <h3 className="text-lg font-semibold text-primary mb-1">Test API JSONCargo</h3>
            <p className="text-tertiary text-sm mb-6">
                Esegui una chiamata di test per BL number o container number e visualizza le response di ogni step
                (BOL, container, vessel finder, vessel/basic).
            </p>

            {/* Form */}
            <div className="flex flex-wrap gap-6 mb-6">
                <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-primary">Tipo test</span>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="testType"
                                checked={testType === "bl"}
                                onChange={() => setTestType("bl")}
                                className="rounded-full border-secondary"
                            />
                            <span className="text-sm">BL number</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="testType"
                                checked={testType === "container"}
                                onChange={() => setTestType("container")}
                                className="rounded-full border-secondary"
                            />
                            <span className="text-sm">Container number</span>
                        </label>
                    </div>
                </div>
                {testType === "bl" && (
                    <div className="min-w-[200px]">
                        <Input
                            label="BL number"
                            value={blNumber}
                            onChange={setBlNumber}
                            placeholder="es. 266402015"
                        />
                    </div>
                )}
                <div className="min-w-[200px]">
                    <Input
                        label={testType === "bl" ? "Carrier (obbligatorio)" : "Carrier"}
                        value={shippingLine}
                        onChange={setShippingLine}
                        placeholder="es. MAERSK"
                    />
                </div>
                <div className="min-w-[200px]">
                    <Input
                        label={testType === "container" ? "Container number" : "Container (opzionale, filtra)"}
                        value={containerNumber}
                        onChange={setContainerNumber}
                        placeholder="es. MMAU1315054"
                    />
                </div>
                <div className="flex items-end pb-2">
                    <Checkbox
                        isSelected={noCache}
                        onChange={setNoCache}
                        label="No cache"
                    />
                </div>
                <div className="flex items-end">
                    <Button
                        size="md"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={!canSubmit || testRun.isPending}
                        iconLeading={Zap}
                    >
                        {testRun.isPending ? "Esecuzione..." : "Esegui test"}
                    </Button>
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="mb-6 p-4 rounded-lg border border-error-200 bg-error-50 flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-error-primary shrink-0 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-error-primary">Errore</p>
                        <p className="text-sm text-primary">
                            {error instanceof Error ? error.message : String(error)}
                        </p>
                    </div>
                </div>
            )}

            {/* Results */}
            {result && !error && (
                <div className="space-y-3">
                    <p className="text-sm font-medium text-primary">
                        {result.steps.length} step — tipo: {result.type}
                    </p>
                    <div className="space-y-2">
                        {result.steps.map((step: TrackingTestStep) => {
                            const isExpanded = expandedSteps.has(step.id);
                            return (
                                <div
                                    key={step.id}
                                    className="rounded-lg border border-secondary bg-secondary/20 overflow-hidden"
                                >
                                    <button
                                        type="button"
                                        onClick={() => toggleStep(step.id)}
                                        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-secondary/30 transition-colors"
                                    >
                                        <span className="flex items-center gap-2">
                                            {isExpanded ? (
                                                <ChevronDown className="h-4 w-4 text-tertiary" />
                                            ) : (
                                                <ChevronRight className="h-4 w-4 text-tertiary" />
                                            )}
                                            <span className="font-medium text-primary">{step.label}</span>
                                            {step.error && (
                                                <Badge color="error" size="sm">
                                                    Errore
                                                </Badge>
                                            )}
                                        </span>
                                    </button>
                                    {isExpanded && (
                                        <div className="px-4 pb-4 pt-0 border-t border-secondary">
                                            {step.error && (
                                                <p className="text-sm text-error-primary mb-3">{step.error}</p>
                                            )}
                                            {step.id.startsWith("vessel_finder") &&
                                            (() => {
                                                const p = step.payload;
                                                const fromCache =
                                                    p &&
                                                    typeof p === "object" &&
                                                    "_from_cache" in p &&
                                                    (p as { _from_cache?: boolean })._from_cache === true;
                                                return fromCache ? (
                                                    <p className="text-sm text-tertiary mb-3">
                                                        Fonte: cache DB (vessel_registry) — nessuna chiamata API.
                                                    </p>
                                                ) : null;
                                            })()}
                                            {treeViewStepIds.has(step.id) ? (
                                                <>
                                                    <PayloadViewer payload={step.payload} defaultExpanded={true} />
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setTreeViewStepIds((prev) => {
                                                                const next = new Set(prev);
                                                                next.delete(step.id);
                                                                return next;
                                                            })
                                                        }
                                                        className="mt-2 text-xs text-brand-primary hover:underline"
                                                    >
                                                        Mostra JSON raw
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setTreeViewStepIds((prev) => new Set(prev).add(step.id))
                                                        }
                                                        className="text-xs text-brand-primary hover:underline mb-2"
                                                    >
                                                        Mostra ad albero
                                                    </button>
                                                    <pre className="text-xs font-mono bg-secondary/50 p-3 rounded-lg overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap break-all">
                                                        {JSON.stringify(step.payload, null, 2)}
                                                    </pre>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {!result && !testRun.isPending && !error && (
                <p className="text-sm text-tertiary">Esegui un test per vedere le response degli step.</p>
            )}
        </div>
    );
}

// ============================================================================
// UsageStatsBar
// ============================================================================

function UsageStatsBar({
    usage,
}: {
    usage: { requests_total: number; requests_made: number; requests_available: number; total_cost_this_month: number; monthly_budget: number; usage_percentage: number };
}) {
    return (
        <div className="rounded-lg border border-secondary bg-primary p-4">
            <div className="flex flex-wrap items-center gap-6">
                <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-primary">
                            Chiamate API
                        </span>
                        <span className="text-sm text-tertiary">
                            {usage.requests_made.toLocaleString()} / {usage.requests_total.toLocaleString()}
                        </span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-secondary">
                        <div
                            className={`h-2 rounded-full transition-all ${usageBarColor(usage.usage_percentage)}`}
                            style={{ width: `${Math.min(usage.usage_percentage, 100)}%` }}
                        />
                    </div>
                </div>
                <div className="text-center">
                    <div className="text-lg font-semibold text-primary">
                        {formatCost(usage.total_cost_this_month)}
                    </div>
                    <div className="text-xs text-tertiary">
                        / {formatCost(usage.monthly_budget)} budget
                    </div>
                </div>
                <div className="text-center">
                    <div className="text-lg font-semibold text-primary">
                        {usage.requests_available.toLocaleString()}
                    </div>
                    <div className="text-xs text-tertiary">rimanenti</div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// SyncConfirmDialog
// ============================================================================

function SyncConfirmDialog({
    estimate,
    isEstimateLoading,
    isSyncing,
    syncResult,
    onConfirm,
    onClose,
}: {
    estimate: SyncEstimateType | undefined;
    isEstimateLoading: boolean;
    isSyncing: boolean;
    syncResult: SyncResult | null;
    onConfirm: () => void;
    onClose: () => void;
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-primary rounded-xl shadow-lg p-6 max-w-lg mx-4 w-full max-h-[80vh] overflow-y-auto">
                {syncResult ? (
                    <>
                        <h3 className="text-lg font-semibold text-primary mb-4">Risultato Sync</h3>
                        <div className="space-y-3 mb-4">
                            <div className="flex items-center gap-3">
                                <Badge color="success" size="sm">{syncResult.successful} OK</Badge>
                                {syncResult.failed > 0 && (
                                    <Badge color="error" size="sm">{syncResult.failed} falliti</Badge>
                                )}
                                {syncResult.skipped > 0 && (
                                    <Badge color="gray" size="sm">{syncResult.skipped} skippati</Badge>
                                )}
                            </div>
                            <p className="text-sm text-tertiary">
                                {syncResult.api_calls_made} chiamate API ({formatCost(syncResult.total_cost)}) in {(syncResult.duration_ms / 1000).toFixed(1)}s
                            </p>
                            {syncResult.results.length > 0 && (
                                <div className="space-y-1 mt-2">
                                    {syncResult.results.map((r) => (
                                        <div key={r.container_id} className="flex items-center gap-2 text-sm">
                                            {r.success ? (
                                                <CheckCircle className="h-4 w-4 text-success-primary shrink-0" />
                                            ) : (
                                                <XCircle className="h-4 w-4 text-error-primary shrink-0" />
                                            )}
                                            <span className="text-primary font-mono text-xs">
                                                {r.container_number || "N/A"}
                                            </span>
                                            {r.success ? (
                                                <span className="text-tertiary">
                                                    {r.events_created} eventi{r.status_updated ? ", status aggiornato" : ""}
                                                </span>
                                            ) : (
                                                <span className="text-error-primary">{r.error}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex justify-end">
                            <Button size="md" color="secondary" onClick={onClose}>
                                Chiudi
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <h3 className="text-lg font-semibold text-primary mb-4">Conferma Sync</h3>
                        {isEstimateLoading ? (
                            <div className="py-6 text-center text-tertiary">Stima in corso...</div>
                        ) : estimate ? (
                            <div className="space-y-4 mb-4">
                                <div className="rounded-lg border border-secondary bg-secondary/30 p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Zap className="h-5 w-5 text-warning" />
                                        <span className="font-semibold text-primary">
                                            {estimate.api_calls_required} chiamate API
                                        </span>
                                    </div>
                                    <p className="text-sm text-tertiary">
                                        Costo stimato: <span className="font-medium text-primary">{formatCost(estimate.estimated_cost)}</span>
                                    </p>
                                </div>
                                {estimate.containers_to_sync.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-primary mb-1">
                                            Container da sincronizzare ({estimate.containers_to_sync.length}):
                                        </p>
                                        <div className="space-y-0.5 max-h-32 overflow-y-auto">
                                            {estimate.containers_to_sync.map((c) => (
                                                <div key={c.container_id} className="flex items-center gap-2 text-sm">
                                                    <CheckCircle className="h-3.5 w-3.5 text-success-primary shrink-0" />
                                                    <span className="font-mono text-xs text-primary">{c.container_number || "N/A"}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {estimate.containers_skipped.length > 0 && (
                                    <div>
                                        <p className="text-sm font-medium text-primary mb-1">
                                            Container skippati ({estimate.containers_skipped.length}):
                                        </p>
                                        <div className="space-y-0.5 max-h-32 overflow-y-auto">
                                            {estimate.containers_skipped.map((c) => (
                                                <div key={c.container_id} className="flex items-center gap-2 text-sm">
                                                    <XCircle className="h-3.5 w-3.5 text-tertiary shrink-0" />
                                                    <span className="font-mono text-xs text-tertiary">{c.container_number || "N/A"}</span>
                                                    <span className="text-xs text-tertiary">— {c.reason}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {estimate.api_calls_required > 15 && (
                                    <div className="flex items-center gap-2 text-sm text-warning">
                                        <AlertTriangle className="h-4 w-4 shrink-0" />
                                        Operazione lunga: potrebbe richiedere fino a {Math.ceil(estimate.api_calls_required * 2)}s
                                    </div>
                                )}
                            </div>
                        ) : null}
                        <div className="flex gap-3 justify-end">
                            <Button size="md" color="secondary" onClick={onClose} disabled={isSyncing}>
                                Annulla
                            </Button>
                            <Button
                                size="md"
                                color="primary"
                                onClick={onConfirm}
                                disabled={isSyncing || isEstimateLoading || !estimate || estimate.api_calls_required === 0}
                                iconLeading={RefreshCw05}
                            >
                                {isSyncing ? "Sincronizzazione..." : "Conferma Sync"}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ============================================================================
// ContainerSubTable
// ============================================================================

function ContainerSubTable({
    companyId,
    onSyncContainer,
}: {
    companyId: string;
    onSyncContainer: (containerId: string) => void;
}) {
    const { data, isLoading } = useTrackingCompanyContainers(companyId);

    if (isLoading) {
        return (
            <div className="py-4 text-center text-tertiary text-sm">
                Caricamento container...
            </div>
        );
    }

    if (!data || data.containers.length === 0) {
        return (
            <div className="py-4 text-center text-tertiary text-sm">
                Nessun container attivo
            </div>
        );
    }

    return (
        <div className="bg-secondary/20 rounded-lg mx-2 mb-2">
            <div className="px-4 py-2 text-xs text-tertiary border-b border-secondary">
                {data.total} container attivi, {data.trackable} sincronizzabili
            </div>
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-xs text-tertiary border-b border-secondary">
                        <th className="text-left px-4 py-2 font-medium">Container</th>
                        <th className="text-left px-4 py-2 font-medium">Status</th>
                        <th className="text-left px-4 py-2 font-medium">BL</th>
                        <th className="text-left px-4 py-2 font-medium">Carrier</th>
                        <th className="text-left px-4 py-2 font-medium">ETA</th>
                        <th className="text-left px-4 py-2 font-medium">Trackable</th>
                        <th className="text-left px-4 py-2 font-medium">Ultimo Sync</th>
                        <th className="text-right px-4 py-2 font-medium">Azione</th>
                    </tr>
                </thead>
                <tbody>
                    {data.containers.map((c) => (
                        <ContainerRow key={c.container_id} container={c} onSync={onSyncContainer} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ContainerRow({
    container: c,
    onSync,
}: {
    container: ContainerTrackingInfo;
    onSync: (containerId: string) => void;
}) {
    return (
        <tr className="border-b border-secondary/50 last:border-0 hover:bg-secondary/30">
            <td className="px-4 py-2">
                <span className="font-mono text-xs text-primary">{c.container_number || "—"}</span>
            </td>
            <td className="px-4 py-2">
                <Badge color={statusBadgeColor(c.status)} size="sm">{c.status}</Badge>
            </td>
            <td className="px-4 py-2">
                <span className="text-xs text-tertiary">{c.bl_number || "—"}</span>
            </td>
            <td className="px-4 py-2">
                <span className="text-xs text-tertiary">{c.carrier_name || "—"}</span>
            </td>
            <td className="px-4 py-2">
                <span className="text-xs text-tertiary">
                    {c.estimated_arrival_date
                        ? new Date(c.estimated_arrival_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
                        : "—"}
                </span>
            </td>
            <td className="px-4 py-2">
                {c.can_track ? (
                    <CheckCircle className="h-4 w-4 text-success-primary" />
                ) : c.cannot_track_reason ? (
                    <span className="group/tip relative flex items-center gap-1 cursor-default">
                        <XCircle className="h-4 w-4 text-error-primary shrink-0" />
                        <span className="text-xs text-error-primary truncate max-w-[120px]">
                            {c.cannot_track_reason}
                        </span>
                        <span className="pointer-events-none absolute bottom-full left-0 mb-1.5 hidden rounded-lg bg-primary-solid px-3 py-2 text-xs font-semibold text-white shadow-lg whitespace-normal max-w-[280px] group-hover/tip:block z-50">
                            {c.cannot_track_reason}
                        </span>
                    </span>
                ) : (
                    <XCircle className="h-4 w-4 text-error-primary" />
                )}
            </td>
            <td className="px-4 py-2">
                <div className="flex items-center gap-1.5">
                    {c.last_sync_success === true && <CheckCircle className="h-3.5 w-3.5 text-success-primary" />}
                    {c.last_sync_success === false && (
                        <span title={c.last_sync_error || undefined}>
                            <XCircle className="h-3.5 w-3.5 text-error-primary" />
                        </span>
                    )}
                    <span className="text-xs text-tertiary">{formatRelativeTime(c.last_tracked_at)}</span>
                </div>
            </td>
            <td className="px-4 py-2 text-right">
                <Button size="sm" color="secondary" iconLeading={RefreshCw05} onClick={() => onSync(c.container_id)}>
                    Sync
                </Button>
            </td>
        </tr>
    );
}

// ============================================================================
// Main Component
// ============================================================================

export function TrackingManagementTab() {
    // Filter state
    const [search, setSearch] = useState("");
    const [companyTypeFilter, setCompanyTypeFilter] = useState("");
    const [expandedCompanyId, setExpandedCompanyId] = useState<string | null>(null);

    // Sync dialog state
    const [syncDialog, setSyncDialog] = useState<{
        type: "company" | "container";
        companyId?: string;
        containerId?: string;
    } | null>(null);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

    // Debounced search
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleSearchChange = useCallback((value: string) => {
        setSearch(value);
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(value), 300);
    }, []);

    // Filters object
    const filters: AdminTrackingFilters = useMemo(
        () => ({
            ...(companyTypeFilter ? { company_type: companyTypeFilter } : {}),
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
        }),
        [companyTypeFilter, debouncedSearch]
    );

    // Data queries
    const { data, isLoading } = useTrackingCompanies(filters);

    // Sync estimate
    const estimateParams = useMemo(() => {
        if (!syncDialog) return { companyId: undefined, containerId: undefined };
        return {
            companyId: syncDialog.type === "company" ? syncDialog.companyId : undefined,
            containerId: syncDialog.type === "container" ? syncDialog.containerId : undefined,
        };
    }, [syncDialog]);
    const { data: estimate, isLoading: isEstimateLoading } = useSyncEstimate(estimateParams);

    // Sync mutations
    const syncCompany = useSyncCompany();
    const syncContainer = useSyncContainer();
    const isSyncing = syncCompany.isPending || syncContainer.isPending;

    // Handlers
    const handleSyncCompanyClick = useCallback((companyId: string) => {
        setSyncResult(null);
        setSyncDialog({ type: "company", companyId });
    }, []);

    const handleSyncContainerClick = useCallback((containerId: string) => {
        setSyncResult(null);
        setSyncDialog({ type: "container", containerId });
    }, []);

    const handleSyncConfirm = useCallback(async () => {
        if (!syncDialog) return;
        try {
            let result: SyncResult;
            if (syncDialog.type === "company" && syncDialog.companyId) {
                result = await syncCompany.mutateAsync(syncDialog.companyId);
            } else if (syncDialog.type === "container" && syncDialog.containerId) {
                result = await syncContainer.mutateAsync(syncDialog.containerId);
            } else {
                return;
            }
            setSyncResult(result);
        } catch {
            // Error handled by mutation
        }
    }, [syncDialog, syncCompany, syncContainer]);

    const handleDialogClose = useCallback(() => {
        setSyncDialog(null);
        setSyncResult(null);
    }, []);

    const toggleExpand = useCallback((companyId: string) => {
        setExpandedCompanyId((prev) => (prev === companyId ? null : companyId));
    }, []);

    return (
        <div className="space-y-6">
            {/* Usage Stats Bar */}
            {data?.usage && <UsageStatsBar usage={data.usage} />}

            {/* Main Card */}
            <div className="rounded-lg border border-secondary bg-primary p-6">
                <h3 className="text-lg font-semibold text-primary mb-1">Tracking Management</h3>
                <p className="text-tertiary text-sm mb-6">
                    Gestione granulare delle chiamate JSONCargo API per azienda e container.
                    Ogni chiamata costa $0.02.
                </p>

                {/* Filters */}
                <div className="flex flex-wrap items-start gap-4 mb-6">
                    <div className="flex-1 min-w-[200px] max-w-[320px]">
                        <Input
                            label="Cerca azienda"
                            value={search}
                            onChange={handleSearchChange}
                            placeholder="Nome azienda..."
                            icon={SearchLg}
                        />
                    </div>
                    <Select
                        label="Tipo azienda"
                        items={COMPANY_TYPE_OPTIONS}
                        selectedKey={companyTypeFilter || "all"}
                        onSelectionChange={(key) => setCompanyTypeFilter((key as string) === "all" ? "" : (key as string))}
                        size="md"
                        className="min-w-[160px]"
                    >
                        {(item) => (
                            <Select.Item key={item.id} id={item.id}>
                                {item.label}
                            </Select.Item>
                        )}
                    </Select>
                </div>

                {/* Summary counts */}
                {data && (
                    <div className="flex items-center gap-4 mb-4 text-sm text-tertiary">
                        <span>{data.total_companies} aziende</span>
                        <span>{data.total_active_containers} container attivi</span>
                        <span>{data.total_trackable_containers} sincronizzabili</span>
                    </div>
                )}

                {/* Companies List */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-12 text-tertiary">
                        Caricamento...
                    </div>
                ) : !data || data.companies.length === 0 ? (
                    <div className="py-12 text-center text-tertiary text-sm">
                        Nessuna azienda con container attivi trovata.
                    </div>
                ) : (
                    <div className="rounded-xl border border-secondary overflow-hidden">
                        {/* Header */}
                        <div className="grid grid-cols-[2rem_1fr_6rem_6rem_8rem_6rem_5rem_8rem] gap-2 items-center px-4 py-2.5 bg-secondary/40 text-xs font-medium text-tertiary border-b border-secondary">
                            <span />
                            <span>Azienda</span>
                            <span>Tipo</span>
                            <span>Container</span>
                            <span>API Calls (mese)</span>
                            <span>Ultimo Track</span>
                            <span>Auto</span>
                            <span className="text-right">Azioni</span>
                        </div>
                        {/* Rows */}
                        {data.companies.map((company) => (
                            <CompanyRowGroup
                                key={company.company_id}
                                company={company}
                                isExpanded={expandedCompanyId === company.company_id}
                                onToggleExpand={toggleExpand}
                                onSyncCompany={handleSyncCompanyClick}
                                onSyncContainer={handleSyncContainerClick}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Test API JSONCargo */}
            <TrackingTestSection />

            {/* Sync Confirmation Dialog */}
            {syncDialog && (
                <SyncConfirmDialog
                    estimate={estimate}
                    isEstimateLoading={isEstimateLoading}
                    isSyncing={isSyncing}
                    syncResult={syncResult}
                    onConfirm={handleSyncConfirm}
                    onClose={handleDialogClose}
                />
            )}
        </div>
    );
}

// ============================================================================
// CompanyRowGroup (row + expandable sub-table)
// ============================================================================

function CompanyRowGroup({
    company,
    isExpanded,
    onToggleExpand,
    onSyncCompany,
    onSyncContainer,
}: {
    company: CompanyTrackingInfo;
    isExpanded: boolean;
    onToggleExpand: (companyId: string) => void;
    onSyncCompany: (companyId: string) => void;
    onSyncContainer: (containerId: string) => void;
}) {
    const ChevronIcon = isExpanded ? ChevronDown : ChevronRight;

    return (
        <div className="border-b border-secondary last:border-0">
            {/* Company row */}
            <div
                className="grid grid-cols-[2rem_1fr_6rem_6rem_8rem_6rem_5rem_8rem] gap-2 items-center px-4 py-3 hover:bg-secondary/20 cursor-pointer transition-colors"
                onClick={() => onToggleExpand(company.company_id)}
            >
                <button
                    className="p-0.5 rounded hover:bg-secondary transition-colors"
                    aria-label={isExpanded ? "Comprimi" : "Espandi"}
                >
                    <ChevronIcon className="h-4 w-4 text-tertiary" />
                </button>
                <span className="text-sm font-medium text-primary truncate">{company.company_name}</span>
                <div>
                    <Badge color={companyTypeBadgeColor(company.company_type)} size="sm">
                        {company.company_type}
                    </Badge>
                </div>
                <div>
                    <span className="text-sm text-primary">{company.active_container_count}</span>
                    <span className="text-xs text-tertiary ml-1">
                        ({company.trackable_container_count})
                    </span>
                </div>
                <div>
                    <span className="text-sm text-primary">{company.api_calls_this_month}</span>
                    <span className="text-xs text-tertiary ml-1">
                        ({formatCost(company.api_cost_this_month)})
                    </span>
                </div>
                <span className="text-sm text-tertiary">
                    {formatRelativeTime(company.last_tracked_at)}
                </span>
                <div>
                    <Badge color={company.tracking_auto_refresh_enabled ? "success" : "gray"} size="sm">
                        {company.tracking_auto_refresh_enabled ? "ON" : "OFF"}
                    </Badge>
                </div>
                <div className="text-right" onClick={(e) => e.stopPropagation()}>
                    <Button
                        size="sm"
                        color="secondary"
                        iconLeading={RefreshCw05}
                        onClick={() => onSyncCompany(company.company_id)}
                        disabled={company.trackable_container_count === 0}
                    >
                        Sync All ({company.trackable_container_count})
                    </Button>
                </div>
            </div>
            {/* Expanded container list */}
            {isExpanded && (
                <ContainerSubTable companyId={company.company_id} onSyncContainer={onSyncContainer} />
            )}
        </div>
    );
}
